<template>
  <div>
    <AdvicesArticleModule :module-data="moduleData" data-luko-tracking="Guide_uid" />
  </div>
</template>

<script>
import { pipe, partition } from '@fxts/core'

import { formatMetaDatas } from '~/utils/metaData'
import AdvicesArticleModule from '~/modules/AdvicesArticle'
import { getNavCtaLinkByTagName } from '~/utils/navbarCta'

const getArticles = async ({ app, $prismic, params }, data) => {
  // prevent causing an error when no category is defined in prismic
  if (!data?.category_page?.id) return []

  const { results } = await $prismic.api.query(
    [
      $prismic.predicates.at('document.type', 'advice_page'),
      $prismic.predicates.at('my.advice_page.category_page', data.category_page.id),
      $prismic.predicates.not('my.advice_page.uid', params.uid),
    ],
    {
      lang: app.i18n.localeProperties.prismicIso,
      pageSize: 6,
    }
  )
  return results
}

const getAdviceData = async ({ app, $prismic, params, store }) => {
  const {
    data,
    last_publication_date: lastPublication,
    first_publication_date: firstPublication,
    alternate_languages,
    tags,
  } = await $prismic.api.getByUID('advice_page', params.uid, {
    lang: app.i18n.localeProperties.prismicIso,
  })

  store.dispatch('setAlternateLanguages', alternate_languages)

  return {
    data,
    lastPublication,
    firstPublication,
    tags,
  }
}

const getArticleDisclaimer = async ({ app, $prismic }) => {
  try {
    const { data } = await $prismic.api.getSingle('article_disclaimer', {
      lang: app.i18n.localeProperties.prismicIso,
    })
    return data
  } catch (e) {
    return {}
  }
}

export default {
  head() {
    if (!this.moduleData.hero) return {}

    const { title, description, image } = this.moduleData.hero

    const defaultMeta = {
      meta_description: '',
      open_graph_title: title[0].text,
      open_graph_description: description[0].text,
      open_graph_image: image.url,
      open_graph_type: 'article',
      twitter_title: title[0].text,
      twitter_description: description[0].text,
      twitter_image: image.url,
    }
    return formatMetaDatas(this.metaDatas, this.$route.path, defaultMeta)
  },

  jsonld() {
    const fullPath = `${process.env.URL}${this.$route.fullPath}/`.replace(/\/\/$/, '/')
    const headline = this.moduleData.hero.title[0].text
    const image = this.moduleData.hero.image.url ? [this.moduleData.hero.image.url] : undefined
    const datePublished = this.moduleData.firstPublication
    const dateModified = this.moduleData.lastPublication

    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': fullPath,
      },
      headline,
      image,
      datePublished,
      dateModified,
      author: {
        '@type': 'Organization',
        name: 'Luko',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Luko',
        url: `${process.env.URL}/`.replace(/\/\/$/, '/'),
        logo: {
          '@type': 'ImageObject',
          url: 'https://luko.cdn.prismic.io/luko/ab601311-bb31-4cfe-a17e-70c4eec37553_Logo.svg',
        },
      },
    }
  },

  components: {
    AdvicesArticleModule,
  },

  async asyncData({ $prismic, store, params, app, error }) {
    try {
      const { data, lastPublication, firstPublication, tags } = await getAdviceData({
        app,
        $prismic,
        params,
        store,
      })
      const articles = await getArticles({ app, $prismic, params }, data)
      const articleDisclaimer = await getArticleDisclaimer({ app, $prismic })

      // Set navbar link depending on tag name
      store.commit('SET_NAV_CTA', getNavCtaLinkByTagName(tags, app.i18n.localeProperties.iso))

      // Set AdivcePage category uid
      store.commit('SET_PAGE_CATEGORY', data.category_page.uid)

      // Force to set theme for navbar and footer
      store.commit('page/UPDATE', { primary: { theme: '2021Q4-pink' } })

      // Insert Newsletter at the end of each article (Only for fr-fr)
      data.body1 = pipe([data.body1, 0], ([body1, count]) => {
        const NewsletterSlice = {
          variation: 'default-slice',
          slice_type: 'news_letter',
          primary: {
            title: [
              {
                type: 'paragraph',
                text: app.i18n.t('Newsletter.AdviceArticle.title'),
              },
            ],
            placeholder: app.i18n.t('Newsletter.AdviceArticle.placeholder'),
            ctaText: app.i18n.t('Newsletter.AdviceArticle.ctaText'),
            description: [
              {
                type: 'paragraph',
                text: app.i18n.t('Newsletter.AdviceArticle.description'),
              },
            ],
          },
        }
        const res = partition((slice) => {
          if (slice.slice_type === 'second_title') count++
          return count < 2
        }, body1)
        return [...res[0], NewsletterSlice, ...res[1]]
      })

      return {
        moduleData: {
          general: data,
          lastPublication,
          firstPublication,
          authorInfos: {
            picture: data.author_picture,
            name: data.author_name,
            lastUpdate: data.last_update,
          },
          articleSlices: data.body1 || [],
          hero: {
            title: data.hero_title,
            description: data.hero_description,
            image: data.hero_image,
            cta: {
              text: data.hero_cta_text,
              link: data.hero_cta_link,
            },
            linkList: data.hero_link_list,
            categoryUid: data.category_page.uid,
          },
          faq: {
            title: data.faq_title,
            list: data.questions_list,
          },
          articles,
          articleDisclaimer,
        },
        metaDatas: data.body || [],
      }
    } catch (e) {
      console.log(e)
      error({ statusCode: 500, message: 'Error in asyncData' })
    }
  },
}
</script>
